import { MARKET, SURCHARGE_TYPE } from '@nandosaus/constants';
import { formatPointsDisplay } from '@nandosaus/state-management/lib/util/formatter';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box, Flex } from '../grid';
import { P } from '../typography/new';
import { pricesPropTypes } from '../../prop-types';
import { useMarket } from '../../hooks/use-market';

const OrderTotals = ({
  deliveryCost,
  deliveryLabelColor,
  subtotalPrices,
  discountPrices,
  totalPrices,
  showDisclaimer,
  isDiscountApplied,
  deliverySponsor,
  surcharges,
  smallTotal,
  pricePerPerson,
  cateringHeadcount,
  borderTop,
  pointsSpent,
  pointsAmountFormatted,
  hideNutritionDisclaimer,
  ...rest
}) => {
  const market = useMarket();
  const holidaySurcharge = surcharges?.find(surcharge => surcharge.type === SURCHARGE_TYPE.HOLIDAY_SURCHARGE);
  const hasHolidaySurcharge = !!holidaySurcharge;
  const showNutritionDisclaimer = market === MARKET.AUSTRALIA && showDisclaimer && !hideNutritionDisclaimer;

  return (
    <Box
      borderTop={borderTop ? '1px solid' : 'none'}
      borderTopColor="warmWhite3"
      display="grid"
      rowGap="12px"
      py="20px"
      {...rest}
    >
      {subtotalPrices && (
        <LineItem>
          <P variant="small">Subtotal</P>
          <P variant="small">{subtotalPrices.formattedPrice}</P>
        </LineItem>
      )}
      {surcharges.map(surcharge => {
        return (
          <LineItem key={surcharge.type}>
            <P variant="small" color="grey70">
              {surcharge.description}
            </P>
            <P variant="small" color="grey70">
              {surcharge.formattedAmount}
            </P>
          </LineItem>
        );
      })}
      {!!deliveryCost && (
        <LineItem>
          <P variant="small" color={deliveryLabelColor}>
            Delivery
          </P>
          {isDiscountApplied ? (
            <Flex bg="red1" borderRadius="8px">
              <P px="8px" py="4px" variant="standard" fontWeight="bold" color="white">
                Free{deliverySponsor ? ` with ${deliverySponsor.name}` : ''}
              </P>
            </Flex>
          ) : (
            <P variant="small" color={deliveryLabelColor}>
              {deliveryCost}
            </P>
          )}
        </LineItem>
      )}
      {pointsSpent > 0 && (
        <LineItem>
          <Flex alignItems="center" gap="8px">
            <P variant="small-semi-bold">PERi-Perks Points</P>
            <P variant="small">{`${formatPointsDisplay(pointsSpent)} pts`}</P>
          </Flex>
          <P variant="small" color="customerRewards3">{`-${pointsAmountFormatted}`}</P>
        </LineItem>
      )}
      {discountPrices.cents !== 0 && (
        <LineItem>
          <P variant="small" color="orange500">
            Discount
          </P>
          <P variant="small" color="orange500">
            {discountPrices.formattedPrice}
          </P>
        </LineItem>
      )}
      {totalPrices && smallTotal && (
        <LineItem>
          <P variant="small-bold">Total</P>
          <Flex alignItems="center" gap="8px">
            <P variant="small-bold">{totalPrices.formattedPrice}</P>
          </Flex>
        </LineItem>
      )}

      {totalPrices && !smallTotal && (
        <LineItem>
          <P variant="large-bold">Total</P>
          <Flex alignItems="center" gap="8px">
            <P variant="large-bold">{totalPrices.formattedPrice}</P>
          </Flex>
        </LineItem>
      )}

      {pricePerPerson && cateringHeadcount && (
        <Box mt={{ md: '8px' }} display="flex" width="100%" justifyContent="center">
          <P variant="small" color="grey70">
            <strong>{pricePerPerson}</strong> per person based on {cateringHeadcount}{' '}
            {cateringHeadcount > 1 ? `people` : `person`}
          </P>
        </Box>
      )}
      {(hasHolidaySurcharge || showNutritionDisclaimer) && (
        <Box display="grid" pt={{ xs: '4px', md: '12px' }} gap="12px">
          {hasHolidaySurcharge && (
            <P variant="small" color="grey70" textAlign="center">
              A surcharge of {holidaySurcharge.formattedSurcharge} applies on public holidays.
            </P>
          )}
          {showNutritionDisclaimer && (
            <P variant="small" color="grey70" textAlign="center">
              Kilojoule calculations do not include drinks or customisations to menu items. The average adult daily
              energy intake is 8,700 kJ.
            </P>
          )}
        </Box>
      )}
    </Box>
  );
};

const LineItem = styled(Flex)`
  justify-content: space-between;
`;

OrderTotals.propTypes = {
  deliveryCost: PropTypes.string,
  deliveryLabelColor: PropTypes.string,
  discountPrices: pricesPropTypes,
  subtotalPrices: PropTypes.oneOfType([
    pricesPropTypes,
    PropTypes.shape({
      formattedPrices: PropTypes.string,
    }),
  ]),
  totalPrices: pricesPropTypes,
  showDisclaimer: PropTypes.bool,
  surcharges: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      formattedAmount: PropTypes.string,
      formattedSurcharge: PropTypes.string,
    })
  ),

  isDiscountApplied: PropTypes.bool,
  deliverySponsor: PropTypes.shape({
    name: PropTypes.string,
  }),
  smallTotal: PropTypes.bool,
  pricePerPerson: PropTypes.string,
  cateringHeadcount: PropTypes.number,
  borderTop: PropTypes.bool,
  pointsSpent: PropTypes.number,
  pointsAmountFormatted: PropTypes.string,
  hideNutritionDisclaimer: PropTypes.bool,
};

OrderTotals.defaultProps = {
  discountPrices: { points: 0, cents: 0 },
  deliveryCost: null,
  deliveryLabelColor: 'grey70',
  showDisclaimer: true,
  totalPrices: null,
  surcharges: [],
  isDiscountApplied: false,
  deliverySponsor: null,
  smallTotal: false,
  pricePerPerson: null,
  cateringHeadcount: null,
  borderTop: false,
  subtotalPrices: null,
  pointsSpent: 0,
  pointsAmountFormatted: null,
  hideNutritionDisclaimer: false,
};

export { OrderTotals };
